import { useRegionalSettings } from '@context/regionalSettings';

const DEFAULT_LOCALE_ENGLISH_US = 'en-US';
const DEFAULT_CURRENCY_USD = 'USD';
const NO_CURRENCY = 'No Currency';

type FormattedCurrencyProps = {
  readonly value: number;
  readonly locale?: string;
  readonly currency?: string;
};

export const formatCurrency = (value: number): string => {
  const formattedValue = Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return value < 0 ? `(${formattedValue})` : `$${formattedValue}`;
};

// Utility function that doesn't use hooks - safe to use in column format functions
export const formatCurrencyWithLocale = (
  value: number,
  locale: string = DEFAULT_LOCALE_ENGLISH_US,
  currency: string = DEFAULT_CURRENCY_USD
): string => {
  if (!value) {
    value = 0;
  }

  let formattedValue;
  if (currency === NO_CURRENCY) {
    formattedValue = new Intl.NumberFormat([
      locale,
      DEFAULT_LOCALE_ENGLISH_US,
    ]).format(value);
  } else {
    formattedValue = new Intl.NumberFormat(
      [locale, DEFAULT_LOCALE_ENGLISH_US],
      {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'symbol',
      }
    ).format(value);
  }
  return formattedValue;
};

// Component that uses hooks - only use this as a React component
export default function FormattedCurrency({
  value,
  locale: propLocale,
  currency: propCurrency,
}: FormattedCurrencyProps) {
  const context = useRegionalSettings();
  const contextLocale = context.locale;
  const contextCurrency = context.currency;

  const locale = propLocale || contextLocale || DEFAULT_LOCALE_ENGLISH_US;
  const currency = propCurrency || contextCurrency || DEFAULT_CURRENCY_USD;

  return formatCurrencyWithLocale(value, locale, currency);
}
